export const CHUNK_RELOAD_FLAG = 'df-chunk-reload';

const CHUNK_ERROR_PATTERNS = [
	'failed to fetch dynamically imported module',
	'error loading dynamically imported module',
	'importing a module script failed',
	'chunkloaderror'
];

/**
 * A content-hashed lazy chunk that 404s after a deploy surfaces as a dynamic-import
 * TypeError whose wording differs per browser, so match on the known phrasings.
 */
export function isChunkLoadError(error: unknown): boolean {
	if (!error) {
		return false;
	}
	const candidate = (error as { error?: unknown }).error ?? error;
	const message =
		typeof candidate === 'string'
			? candidate
			: ((candidate as { message?: string })?.message ?? '');
	const haystack = message.toLowerCase();
	return CHUNK_ERROR_PATTERNS.some(pattern => haystack.includes(pattern));
}

/**
 * Claims the single reload allowed per browsing session. Returns true at most once,
 * so a chunk that is genuinely missing from the server cannot reload-loop the app.
 * The flag is released by a successful navigation (see AppComponent).
 */
export function claimChunkReload(error: unknown): boolean {
	if (!isChunkLoadError(error)) {
		return false;
	}
	if (sessionStorage.getItem(CHUNK_RELOAD_FLAG)) {
		return false;
	}
	sessionStorage.setItem(CHUNK_RELOAD_FLAG, '1');
	return true;
}

export function releaseChunkReload(): void {
	sessionStorage.removeItem(CHUNK_RELOAD_FLAG);
}

/**
 * Shared with the Tier 1 card. Plain reload, deliberately NOT delegating to index.html's
 * __dfReload: that fetch-then-reload dance is required only for Tier 2 (spec §5.3), where
 * Angular never ran and there is no other recovery code on the page. Here Angular is running,
 * index.html already ships `no-cache` + a real etag (measured: a conditional GET revalidates
 * and 304s), so location.reload() already does the right thing — spec §5.2 calls the fetch
 * wrapper "machinery that treats nothing" for this path. It is also unbounded: on the exact
 * flaky-network conditions this button exists to recover from, an un-awaited fetch with no
 * timeout can leave Reload doing nothing visible until the browser's own network timeout.
 */
export function reloadBustingCache(): void {
	location.reload();
}
